import { useEffect, useState } from "react";
import { useI18n } from "@/context/LanguageContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

export const Nav = () => {
  const { content, t } = useI18n();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const links = [
    { href: "#historia", label: t.nav.story },
    { href: "#informacio", label: t.nav.info },
    { href: "#horaris", label: t.nav.schedule },
    { href: "#allotjament", label: t.nav.stay },
    { href: "#regal", label: t.nav.gift },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 bg-[#F0311B] text-white ${
        scrolled ? "shadow-[0_2px_0_0_#141518]" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 flex items-center justify-between h-14 md:h-20">
        <a href="#top" data-testid="nav-logo" className="flex items-center" aria-label="Ariadna & Wojtek">
          <img
            src={content.logo}
            alt="Ariadna & Wojtek"
            className="h-9 md:h-12 w-auto select-none"
            draggable="false"
          />
        </a>

        <div className="flex items-center gap-2 md:gap-3">
          <LanguageSwitcher />
          <a
            href={content.rsvpUrl}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-rsvp-header"
            className="inline-flex items-center gap-2 bg-white text-[#141518] px-3 md:px-5 py-2 md:py-2.5 text-[10px] md:text-[11px] uppercase tracking-[0.18em] md:tracking-[0.22em] font-bold border-2 border-[#141518] hover:bg-[#7AD5DC] transition-colors"
          >
            <span className="hidden sm:inline">{t.nav.rsvpFull}</span>
            <span className="sm:hidden">{t.nav.rsvp}</span>
          </a>
          <button
            data-testid="nav-toggle"
            onClick={() => setOpen((o) => !o)}
            className="relative w-11 h-11 flex flex-col items-center justify-center gap-1.5"
            aria-label={open ? t.nav.closeMenu : t.nav.openMenu}
            aria-expanded={open}
          >
            <span className={`block w-6 h-[2px] bg-white transition-transform duration-300 ${open ? "translate-y-[7px] rotate-45" : ""}`} />
            <span className={`block w-6 h-[2px] bg-white transition-opacity duration-200 ${open ? "opacity-0" : ""}`} />
            <span className={`block w-6 h-[2px] bg-white transition-transform duration-300 ${open ? "-translate-y-[9px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <div
        data-testid="nav-menu"
        className={`fixed inset-0 top-14 md:top-20 bg-[#F0311B] border-t-2 border-[#141518] transition-all duration-500 ${
          open ? "opacity-100 pointer-events-auto translate-y-0" : "opacity-0 pointer-events-none -translate-y-2"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-6 md:px-8 py-8 md:py-12 h-full flex flex-col">
          <ul className="space-y-2 md:space-y-3">
            {links.map((l, i) => (
              <li
                key={l.href}
                style={{ transitionDelay: open ? `${100 + i * 60}ms` : "0ms" }}
                className={`transition-all duration-500 ${open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}
              >
                <a
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-link-${l.href.slice(1)}`}
                  className="group block font-display font-bold text-[14vw] md:text-6xl lg:text-7xl leading-[1] text-white hover:italic hover:text-[#7AD5DC] transition-all"
                >
                  <span className="inline-block transition-transform group-hover:translate-x-2">
                    {l.label}
                  </span>
                </a>
              </li>
            ))}
          </ul>

          <div
            style={{ transitionDelay: open ? "500ms" : "0ms" }}
            className={`mt-auto pt-10 transition-all duration-500 flex flex-col md:flex-row md:items-end md:justify-between gap-6 ${
              open ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"
            }`}
          >
            <div>
              <div className="font-condensed uppercase tracking-[0.25em] text-[10px] text-white/70">
                ✱ {t.footer.tagline}
              </div>
              <div className="font-display font-bold text-3xl md:text-4xl mt-1 text-white">
                {content.dateLabel}
              </div>
              <div className="font-condensed uppercase tracking-[0.2em] text-[10px] text-white/70 mt-1">
                {t.footer.place}
              </div>
            </div>
            <a
              href={content.rsvpUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              data-testid="nav-rsvp-button"
              className="group inline-flex items-center justify-center gap-3 bg-white text-[#141518] px-6 py-4 text-xs uppercase tracking-[0.22em] font-bold border-2 border-[#141518] shadow-[6px_6px_0_0_#7AD5DC] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[3px_3px_0_0_#7AD5DC] transition-all"
            >
              {t.nav.rsvpFull}
              <span className="transition-transform group-hover:translate-x-1">→</span>
            </a>
          </div>
        </div>
      </div>
    </header>
  );
};
